// src/components/resources/tools/generators/FakeChat/DiscordPreview.tsx

'use client'

import { cn } from '~/lib/utils'
import { useFakeChatState } from './FakeChatContext'
import type { ChatMessage, ChatParticipant } from './FakeChat.types'
import { useResolvedMessages } from './useResolvedMessages'
import { discordNameColor } from './avatarNameColors'

export function normalizeDiscordChannelName(channelName: string | undefined): string {
  const source = channelName === undefined || channelName === '' ? 'general' : channelName
  const normalized = source.replace(/^#/, '').trim().toLowerCase().replace(/\s+/g, '-')
  return normalized === '' ? 'general' : normalized
}

export function normalizeDiscordWorkspaceName(workspaceName: string | undefined): string {
  const normalized = workspaceName?.trim() ?? ''
  return normalized === '' ? 'Campaign Hub' : normalized
}

interface DiscordRowProps {
  message: ChatMessage
  participant?: ChatParticipant
  isContinuation: boolean
  showAvatars: boolean
  showTimestamps: boolean
  showReactions: boolean
}

/**
 * Single Discord message row. The first message in a run shows the avatar,
 * coloured username, and timestamp; continuation messages are indented under
 * the same column with a hover-revealed inline timestamp, exactly like Discord.
 */
function DiscordRow({
  message,
  participant,
  isContinuation,
  showAvatars,
  showTimestamps,
  showReactions
}: DiscordRowProps): JSX.Element {
  const nameColor = discordNameColor(participant?.avatarColor)
  // Discord groups consecutive messages from the same author into a single
  // run: only the first row shows the avatar + username + timestamp, and
  // continuations show just the text (with a hover timestamp in the gutter).
  // This grouping is independent of the avatars toggle — with avatars off the
  // first row still carries the username and continuations stay grouped.
  const showHeader = !isContinuation
  const isBot = participant?.role === 'bot'

  return (
    <div className={cn('group relative flex gap-4 px-4 hover:bg-[#2e3035]', showHeader ? 'mt-[17px]' : 'mt-[2px]')}>
      <div className="w-10 shrink-0">
        {showHeader && showAvatars ? (
          <div
            className={cn(
              'mt-0.5 flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold text-white',
              participant?.avatarColor ?? 'bg-[#5865f2]'
            )}
          >
            {participant?.avatarInitials ?? '?'}
          </div>
        ) : (
          <span className="block w-10 select-none pt-[2px] text-right text-[10px] leading-[22px] text-[#a3a6aa] opacity-0 group-hover:opacity-100">
            {showTimestamps ? clockOrDefault(message.timestamp) : ''}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1 pb-[2px]">
        {showHeader ? (
          <div className="flex items-center gap-2">
            <span className={cn('text-[15px] font-medium leading-[22px]', nameColor)}>
              {participant?.displayName ?? 'Unknown'}
            </span>
            {isBot ? (
              <span className="flex items-center gap-0.5 rounded bg-[#5865f2] px-[4px] py-[1px] text-[10px] font-semibold uppercase leading-none text-white">
                <svg viewBox="0 0 16 16" className="h-[9px] w-[9px]" fill="currentColor" aria-hidden>
                  <path d="M8 1 2 4v4c0 3.5 2.6 6 6 7 3.4-1 6-3.5 6-7V4L8 1z" />
                </svg>
                App
              </span>
            ) : null}
            {showTimestamps ? (
              <span className="text-[12px] leading-[22px] text-[#a3a6aa]">Today at {clockOrDefault(message.timestamp)}</span>
            ) : null}
          </div>
        ) : null}
        <p className="whitespace-pre-wrap break-words text-[15px] leading-[22px] text-[#dbdee1]">{message.text}</p>
        {message.attachments?.map(attachment => (
          <div
            key={attachment.id}
            className="mt-1 max-w-[420px] rounded-[8px] border-l-4 border-[#5865f2] bg-[#2b2d31] px-3 py-2 text-[13px] text-[#dbdee1]"
          >
            <p className="font-semibold text-white">{attachment.title}</p>
            {attachment.description ? <p className="mt-0.5 text-[12px] text-[#b5bac1]">{attachment.description}</p> : null}
          </div>
        ))}
        {showReactions && message.reactions.length > 0 ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {message.reactions.map(reaction => (
              <span
                key={reaction.id}
                className={cn(
                  'inline-flex items-center gap-1 rounded-[8px] border px-[7px] py-[2px] text-[13px]',
                  reaction.reactedByViewer
                    ? 'border-[#5865f2] bg-[#3c3f7a]/40 text-[#c9cdfb]'
                    : 'border-transparent bg-[#2b2d31] text-[#b5bac1]'
                )}
              >
                {reaction.emoji}
                <span className="text-[13px] font-medium">{reaction.count}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Discord platform preview — desktop-framed dark UI with the server rail,
 * channel sidebar, channel header, grouped messages with coloured usernames,
 * and the Discord message composer.
 */
export function DiscordPreview(): JSX.Element {
  const { conversation } = useFakeChatState()
  const resolvedMessages = useResolvedMessages(conversation.messages)
  // Discord forces channel names to lowercase-hyphenated; mirror that so any
  // input (and the seeded "Launch sync") reads as an authentic #channel.
  const channelName = normalizeDiscordChannelName(conversation.metadata.channelName)
  // Decorative sidebar channels alongside the active one. Dedupe against the
  // active channel so a user-typed name matching 'general'/'design-sync' (or a
  // cleared name that falls back to 'general') doesn't render a duplicate row.
  const sidebarChannels = [...new Set(['general', channelName, 'design-sync'])]
  const workspaceName = normalizeDiscordWorkspaceName(conversation.metadata.workspaceName)
  const serverInitial = workspaceName.charAt(0)?.toUpperCase() ?? 'A'
  const onlineMembers = conversation.participants.filter(p => !p.isSelf)

  return (
    <div
      className="flex h-[600px] w-full overflow-hidden rounded-xl border border-black/40 text-[#dbdee1] shadow-2xl"
      style={{ fontFamily: "'gg sans', 'Segoe UI', -apple-system, BlinkMacSystemFont, system-ui, sans-serif" }}
    >
      {/* Server rail */}
      <nav className="flex w-[72px] shrink-0 flex-col items-center gap-2 bg-[#1e1f22] py-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-[18px] bg-[#5865f2] text-lg font-bold text-white">
          {serverInitial}
        </div>
        <span className="h-0.5 w-8 rounded-full bg-[#35373c]" aria-hidden />
        {['#3ba55d', '#faa61a', '#eb459e'].map((c, i) => (
          <div
            key={i}
            className="flex h-12 w-12 items-center justify-center rounded-[24px] bg-[#313338] text-sm font-semibold text-[#dbdee1]"
            style={{ color: c }}
          >
            {String.fromCharCode(66 + i)}
          </div>
        ))}
        <div className="flex h-12 w-12 items-center justify-center rounded-[24px] bg-[#313338] text-2xl font-light text-[#3ba55d]">
          +
        </div>
      </nav>

      {/* Channel sidebar */}
      <aside className="flex w-[210px] shrink-0 flex-col bg-[#2b2d31]">
        <div className="flex h-12 shrink-0 items-center border-b border-black/20 px-4 shadow-sm">
          <span className="truncate text-[15px] font-semibold text-white">{workspaceName}</span>
          <svg viewBox="0 0 24 24" className="ml-auto h-4 w-4 text-[#b5bac1]" fill="none" aria-hidden>
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="flex-1 overflow-hidden px-2 py-3 text-[15px]">
          <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[#8a8e94]">Text channels</p>
          <div className="space-y-[2px]">
            {sidebarChannels.map(c => {
              const active = c === channelName
              return (
                <div
                  key={c}
                  className={cn(
                    'flex items-center gap-1.5 rounded px-2 py-[6px]',
                    active ? 'bg-[#404249] text-white' : 'text-[#8a8e94] hover:bg-[#35373c] hover:text-[#dbdee1]'
                  )}
                >
                  <span className="text-[18px] leading-none text-[#80848e]">#</span>
                  <span className="truncate">{c}</span>
                </div>
              )
            })}
          </div>
        </div>
        {/* User footer */}
        <div className="flex items-center gap-2 bg-[#232428] px-2 py-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#5865f2] text-xs font-semibold text-white">
            You
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-white">You</p>
            <p className="truncate text-[11px] text-[#b5bac1]">Online</p>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col bg-[#313338]">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-black/20 px-4 shadow-sm">
          <span className="shrink-0 text-[20px] leading-none text-[#80848e]">#</span>
          <span className="shrink-0 whitespace-nowrap text-[15px] font-semibold text-white">{channelName}</span>
          <span className="mx-2 h-5 w-px shrink-0 bg-[#3f4147]" aria-hidden />
          <span className="truncate text-[13px] text-[#b5bac1]">{conversation.metadata.channelTopic}</span>
        </header>
        <div className="no-scrollbar flex flex-1 flex-col overflow-auto pb-4">
          <div className="px-4 pt-6">
            <div className="mb-2 flex h-[68px] w-[68px] items-center justify-center rounded-full bg-[#41434a] text-3xl text-[#dbdee1]">
              #
            </div>
            <h2 className="text-[28px] font-bold text-white">Welcome to #{channelName}!</h2>
            <p className="text-[15px] text-[#b5bac1]">This is the start of the #{channelName} channel.</p>
          </div>
          <div className="mt-2">
            {resolvedMessages.map(({ message, participant, isContinuation }) => (
              <DiscordRow
                key={message.id}
                message={message}
                participant={participant}
                isContinuation={isContinuation}
                showAvatars={conversation.metadata.showAvatars}
                showTimestamps={conversation.metadata.showTimestamps}
                showReactions={conversation.metadata.showReactions}
              />
            ))}
          </div>
          {conversation.metadata.typingIndicator ? (
            <div className="mt-2 flex items-center gap-2 px-4 text-[13px] text-[#dbdee1]">
              <span className="flex items-center gap-1">
                <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-[#b5bac1]" />
                <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-[#b5bac1]" style={{ animationDelay: '0.15s' }} />
                <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-[#b5bac1]" style={{ animationDelay: '0.3s' }} />
              </span>
              <span>
                <strong className="text-white">{onlineMembers[0]?.displayName ?? 'Someone'}</strong> is typing…
              </span>
            </div>
          ) : null}
        </div>
        {/* Composer */}
        <div className="px-4 pb-5 pt-1">
          <div className="flex items-center gap-3 rounded-[8px] bg-[#383a40] px-4 py-[11px]">
            <svg viewBox="0 0 24 24" className="h-6 w-6 shrink-0 text-[#b5bac1]" fill="currentColor" aria-hidden>
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v8M8 12h8" stroke="#383a40" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="flex-1 text-[15px] text-[#6d7178]">Message #{channelName}</span>
            <div className="flex items-center gap-3 text-[#b5bac1]">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
                <path d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-3l-4 4v-4H5a2 2 0 0 1-2-2V5z" />
              </svg>
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8.5 14s1.3 1.6 3.5 1.6 3.5-1.6 3.5-1.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="9" cy="10" r="1" fill="currentColor" />
                <circle cx="15" cy="10" r="1" fill="currentColor" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Discord renders the timestamp string as-is ("Today at 9:24 AM"), so this
 * just trims and supplies a default when the message has no timestamp. No
 * reformatting is performed.
 */
function clockOrDefault(timestamp: string): string {
  return timestamp.trim() || '12:00 PM'
}
